"use client";

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Navigation from '@/components/Navigation';
import CountrySelectModal, { useCountry } from '@/components/CountrySelectModal';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Search,
  MapPin,
  CircleDollarSign,
  Sparkles,
  Loader2,
  Package,
  Tag,
  Filter,
  X,
  Globe,
  Images,
  PlayCircle,
  MessageSquare,
  MessageCircle,
  ArrowRight,
  Grid3X3,
  Smartphone,
  Laptop,
  Sofa,
  Shirt,
  BriefcaseBusiness,
  Car,
  Home,
  Utensils,
  Snowflake,
  Zap,
  Watch,
  ShoppingBag,
  Wrench,
  Armchair,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { buildWhatsAppUrl } from '@/lib/whatsapp';
import { getCurrencyLabel } from '@/lib/currency';

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

const MARKET_CATEGORY_IMAGES = [
  {
    keys: ['phone', 'smartphone', 'mobile', 'electronics', 'جوال', 'الكترون', 'إلكترون'],
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=900&auto=format&fit=crop',
    accent: 'from-indigo-600/85 to-slate-950/80',
    Icon: Smartphone,
  },
  {
    keys: ['laptop', 'computer', 'كمبيوتر', 'لابتوب'],
    image: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?q=80&w=900&auto=format&fit=crop',
    accent: 'from-blue-600/85 to-slate-950/80',
    Icon: Laptop,
  },
  {
    keys: ['home', 'furniture', 'أثاث', 'منزل'],
    image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?q=80&w=900&auto=format&fit=crop',
    accent: 'from-amber-600/85 to-slate-950/80',
    Icon: Sofa,
  },
  {
    keys: ['fashion', 'shirt', 'clothes', 'ملابس', 'ازياء', 'أزياء'],
    image: 'https://images.unsplash.com/photo-1445205170230-053b83016050?q=80&w=900&auto=format&fit=crop',
    accent: 'from-pink-600/85 to-slate-950/80',
    Icon: Shirt,
  },
  {
    keys: ['service', 'خدمات', 'work'],
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=900&auto=format&fit=crop',
    accent: 'from-emerald-600/85 to-slate-950/80',
    Icon: BriefcaseBusiness,
  },
  {
    keys: ['car', 'auto', 'سيارة', 'سيارات'],
    image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=900&auto=format&fit=crop',
    accent: 'from-orange-600/85 to-slate-950/80',
    Icon: Car,
  },
  {
    keys: ['kitchen', 'tools', 'أدوات', 'مطبخ'],
    image: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?q=80&w=900&auto=format&fit=crop',
    accent: 'from-cyan-600/70 to-slate-950/80',
    Icon: Utensils,
  },
  {
    keys: ['decor', 'ديكور'],
    image: 'https://images.unsplash.com/photo-1513519245088-0e12902e35ca?q=80&w=900&auto=format&fit=crop',
    accent: 'from-violet-600/70 to-slate-950/80',
    Icon: Armchair,
  },
  {
    keys: ['electrical', 'electric', 'كهرباء'],
    image: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?q=80&w=900&auto=format&fit=crop',
    accent: 'from-blue-600/70 to-slate-950/80',
    Icon: Zap,
  },
  {
    keys: ['cooling', 'a/c', 'تكييف'],
    image: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?q=80&w=900&auto=format&fit=crop',
    accent: 'from-sky-600/70 to-slate-950/80',
    Icon: Snowflake,
  },
  {
    keys: ['brake', 'brakes', 'فرامل'],
    image: 'https://images.unsplash.com/photo-1625047509248-ec889cbff17f?q=80&w=900&auto=format&fit=crop',
    accent: 'from-red-600/70 to-slate-950/80',
    Icon: Wrench,
  },
  {
    keys: ['engine', 'محرك'],
    image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?q=80&w=900&auto=format&fit=crop',
    accent: 'from-slate-700/70 to-slate-950/80',
    Icon: Wrench,
  },
  {
    keys: ['accessory', 'watch', 'ساعة', 'اكسسوار', 'إكسسوار'],
    image: 'https://images.unsplash.com/photo-1523170335258-f5ed11844a49?q=80&w=900&auto=format&fit=crop',
    accent: 'from-stone-600/70 to-slate-950/80',
    Icon: Watch,
  },
];

const MARKET_FALLBACK_ICONS: LucideIcon[] = [
  ShoppingBag,
  Package,
  Home,
  Wrench,
  Sofa,
  Sparkles,
  Tag,
  BriefcaseBusiness,
];

const MARKET_FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1523275335684-37898b6baf30?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1498049794561-7780e7231661?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1483985988355-763728e1935b?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1519710164239-da123dc03ef4?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1503602642458-232111445657?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1542291026-7eec264c27ff?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1524758631624-e2822e304c36?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1542838132-92c53300491e?q=80&w=900&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?q=80&w=900&auto=format&fit=crop',
];

const FALLBACK_MARKET_CATEGORIES = [
  { id: 'electronics', name: 'Electronics', name_ar: 'إلكترونيات', slug: 'electronics' },
  { id: 'cars-accessories', name: 'Cars & Accessories', name_ar: 'سيارات وملحقات', slug: 'cars-accessories' },
  { id: 'home-furniture', name: 'Home & Furniture', name_ar: 'منزل وأثاث', slug: 'home-furniture' },
  { id: 'fashion', name: 'Fashion', name_ar: 'أزياء وملابس', slug: 'fashion' },
  { id: 'services', name: 'Services', name_ar: 'خدمات', slug: 'services' },
  { id: 'tools', name: 'Tools', name_ar: 'أدوات ومعدات', slug: 'tools' },
  { id: 'decor', name: 'Decor', name_ar: 'ديكور', slug: 'decor' },
  { id: 'accessories', name: 'Accessories', name_ar: 'إكسسوارات', slug: 'accessories' },
];

function normalizeCategoryText(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function getMarketCategoryVisual(category: any, index = 0) {
  const text = normalizeCategoryText(`${category?.name || ''} ${category?.name_ar || ''} ${category?.slug || ''} ${category?.icon || ''}`);
  const matched = MARKET_CATEGORY_IMAGES.find((entry) => entry.keys.some((key) => text.includes(key)));
  if (matched) return matched;

  return {
    image: MARKET_FALLBACK_IMAGES[index % MARKET_FALLBACK_IMAGES.length],
    accent: 'from-primary/85 to-slate-950/80',
    Icon: MARKET_FALLBACK_ICONS[index % MARKET_FALLBACK_ICONS.length],
  };
}

export default function Marketplace() {
  const { toast } = useToast();
  const router = useRouter();
  const { country, setCountry } = useCountry();
  const currencyLabel = getCurrencyLabel(country);
  const [publicListings, setPublicListings] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [browseStep, setBrowseStep] = useState<'categories' | 'items'>('categories');
  const [aiSearching, setAiSearching] = useState(false);
  const [countryModalOpen, setCountryModalOpen] = useState(false);

  useEffect(() => {
    fetchCategories(country?.code);
    const query = new URLSearchParams(window.location.search).get('search');
    if (query?.trim()) {
      setSearchQuery(query.trim());
      setBrowseStep('items');
      fetchListings(query.trim());
    } else {
      fetchListings();
    }
  }, []);

  const fetchCategories = async (countryCode?: string) => {
    try {
      const params = new URLSearchParams();
      const resolvedCountry = countryCode ?? country?.code;
      if (resolvedCountry) params.append('country_code', resolvedCountry);
      const url = `${API}/api/marketplace/public-listings/categories/${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url);
      if (!res.ok) {
        setCategories(FALLBACK_MARKET_CATEGORIES);
        return;
      }
      const data = await res.json();
      const rows = data.results || data || [];
      setCategories(rows.length ? rows : FALLBACK_MARKET_CATEGORIES);
    } catch {
      console.warn("Marketplace category API unavailable; using local categories.");
      setCategories(FALLBACK_MARKET_CATEGORIES);
    }
  };

  const fetchListings = async (query = '', categoryId: any = null, countryCode?: string) => {
    setLoading(true);
    try {
      let url = `${API}/api/marketplace/public-listings/`;
      const params = new URLSearchParams();
      if (query) params.append('search', query);
      if (categoryId) params.append('category', categoryId);
      // Apply country filter if a country is selected
      const resolvedCountry = countryCode ?? country?.code;
      if (resolvedCountry) params.append('country_code', resolvedCountry);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const res = await fetch(url);
      if (!res.ok) {
        setPublicListings([]);
        return;
      }
      const data = await res.json();
      setPublicListings(data.results || data || []);
    } catch {
      console.warn("Marketplace listings API unavailable; showing empty listings.");
      setPublicListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAiSearch = async () => {
    if (!searchQuery) {
      fetchListings();
      return;
    }
    setAiSearching(true);
    try {
      const res = await fetch(`${API}/api/marketplace/public-listings/smart-search/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery })
      });
      const data = await res.json();
      if (data.keywords && data.keywords.length > 0) {
        toast({ title: '🤖 بحث ذكي', description: `تم تحليل طلبك إلى كلمات مفتاحية: ${data.keywords.join(', ')}` });
        fetchListings(data.keywords.join(' '));
      } else {
        fetchListings(searchQuery);
      }
    } catch {
      fetchListings(searchQuery);
    } finally {
      setAiSearching(false);
    }
  };

  const handleCategorySelect = (category: any) => {
    setSelectedCategory(category);
    setBrowseStep('items');
    fetchListings(searchQuery, category.id);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
    setBrowseStep('categories');
    fetchListings();
  };

  const extractMediaUrls = (listing: any): string[] => {
    const urls: string[] = [];
    const images = Array.isArray(listing?.images) ? listing.images : [];
    images.forEach((entry: any) => {
      const raw = typeof entry === 'string' ? entry : (entry?.image || entry?.image_url);
      if (typeof raw === 'string' && raw.trim()) urls.push(raw);
    });

    const videos = Array.isArray(listing?.videos) ? listing.videos : [];
    videos.forEach((entry: any) => {
      const raw = typeof entry === 'string' ? entry : (entry?.video || entry?.video_url || entry?.url);
      if (typeof raw === 'string' && raw.trim()) urls.push(raw);
    });

    const directVideo = listing?.video || listing?.video_url;
    if (typeof directVideo === 'string' && directVideo.trim()) urls.push(directVideo);

    return urls;
  };

  const isVideoUrl = (url: string) => {
    const lower = url.toLowerCase();
    return (
      lower.endsWith('.mp4') ||
      lower.endsWith('.mov') ||
      lower.endsWith('.m4v') ||
      lower.endsWith('.webm') ||
      lower.includes('/video/')
    );
  };

  const resolveContactPhone = (listing: any): string | null => {
    const raw = listing?.seller_phone || listing?.contact_phone || listing?.contact_number || listing?.phone || listing?.contact_method;
    if (typeof raw !== 'string' || !raw.trim()) return null;
    const cleaned = raw.replace(/[^\d+\-()\s]/g, '').trim();
    return cleaned || null;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation title="السوق العام (الحراج)" />

      {/* Country Filter Banner */}
      {country?.code && (
        <div className="mx-4 mt-2 mb-0 flex items-center justify-between px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-2xl">
          <div className="flex items-center gap-2">
            <span className="text-lg">{country.emoji}</span>
            <span className="text-sm font-bold text-blue-800">تعرض إعلانات: {country.name_ar}</span>
          </div>
          <button
            onClick={() => setCountryModalOpen(true)}
            className="text-xs text-blue-600 font-bold hover:underline flex items-center gap-1"
          >
            <Globe className="h-3 w-3" /> تغيير
          </button>
        </div>
      )}

      <CountrySelectModal
        isOpen={countryModalOpen}
        onClose={() => setCountryModalOpen(false)}
        onSelect={(c) => {
          setCountry(c);
          setSelectedCategory(null);
          setBrowseStep(searchQuery ? 'items' : 'categories');
          fetchCategories(c.code);
          fetchListings(searchQuery, null, c.code);
        }}
        showAllOption
      />

      <div className="p-4 max-w-7xl mx-auto space-y-6 pb-24">
        {/* Search Bar & AI Search */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
            <Input
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim()) setBrowseStep('items');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  setBrowseStep('items');
                  fetchListings(searchQuery, selectedCategory?.id);
                }
              }}
              className="pr-12 rounded-2xl bg-white border-none shadow-sm h-14 text-lg"
              placeholder="ابحث عن أي شيء (جوال، أثاث، خدمات...)"
            />
          </div>
          <Button
            onClick={handleAiSearch} disabled={aiSearching}
            className="h-14 rounded-2xl px-6 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/25 flex gap-2 font-bold transition-all"
          >
            {aiSearching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            <span className="hidden sm:inline">بحث ذكي</span>
          </Button>
        </div>

        {/* Categories Carousel */}
        {browseStep === 'items' && (
          <div className="space-y-3 rounded-[1.5rem] bg-white p-3 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Badge className="rounded-full bg-primary px-4 py-2 text-white">
                  {selectedCategory?.name_ar || (searchQuery ? 'نتائج البحث' : 'أحدث الإعلانات')}
                </Badge>
              </div>
              <Button variant="outline" className="rounded-2xl shrink-0 gap-2" onClick={clearFilters}>
                <ArrowRight className="h-4 w-4" />
                التصنيفات
              </Button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setBrowseStep('items');
                  fetchListings(searchQuery, null);
                }}
                className={`shrink-0 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                  !selectedCategory ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-primary hover:text-primary'
                }`}
              >
                كل الإعلانات
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat)}
                  className={`shrink-0 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                    selectedCategory?.id === cat.id ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-primary hover:text-primary'
                  }`}
                >
                  {cat.name_ar}
                </button>
              ))}
            </div>
          </div>
        )}

        {browseStep === 'categories' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
              <Grid3X3 className="w-6 h-6 text-primary" />
              تصفح حسب التصنيف
            </h2>
            {(searchQuery || selectedCategory) && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500 hover:text-red-500 flex gap-1">
                <X className="w-3 h-3" /> مسح الفلاتر
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {categories.map((cat, index) => {
              const visual = getMarketCategoryVisual(cat, index);
              const CategoryIcon = visual.Icon;
              return (
                <button key={cat.id} onClick={() => handleCategorySelect(cat)} className="group text-right">
                  <div className="relative h-36 overflow-hidden rounded-[1.5rem] bg-white shadow-sm ring-1 ring-slate-100 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-xl">
                    <img src={visual.image} alt={cat.name_ar} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-slate-950/80 via-slate-950/15 to-transparent" />
                    <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/20 to-transparent" />
                    <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-2xl bg-white/95 text-primary shadow-sm backdrop-blur">
                      <CategoryIcon className="h-4 w-4" />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                      <div className="mb-2 inline-flex h-7 w-7 items-center justify-center rounded-xl bg-white/20 text-white shadow-sm backdrop-blur-md ring-1 ring-white/25">
                        <CategoryIcon className="h-3.5 w-3.5" />
                      </div>
                      <h3 className="truncate text-sm font-black sm:text-base">{cat.name_ar}</h3>
                      <p className="mt-0.5 truncate text-[11px] font-semibold text-white/75">{cat.name}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between rounded-full bg-slate-50 px-3 py-1.5 text-[11px] font-bold text-slate-500">
                    <span>عرض الإعلانات</span>
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary shadow-sm">
                      <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Dynamic Public Listings Section */}
        {browseStep === 'items' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
              <Tag className="w-6 h-6 text-emerald-500" />
              {selectedCategory ? `${selectedCategory.name_ar}` : searchQuery ? `نتائج البحث عن "${searchQuery}"` : 'أحدث الإعلانات'}
            </h2>
            <Link href="/marketplace/new">
              <Button className="rounded-xl h-10 font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20">إضافة إعلان</Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Card key={i} className="h-80 animate-pulse bg-slate-200/50 rounded-[2rem] border-none" />
              ))}
            </div>
          ) : publicListings.length === 0 ? (
            <Card className="rounded-[2.5rem] border-none shadow-sm bg-white p-12 text-center text-slate-400">
              <Package className="w-20 h-20 mx-auto mb-6 opacity-20" />
              <p className="text-xl font-bold text-slate-500">لا توجد إعلانات متاحة حالياً</p>
              <p className="text-sm text-slate-400 mt-2">كن أول من يضيف إعلاناً في هذا التصنيف!</p>
              <Link href="/marketplace/new" className="mt-6 inline-block">
                <Button variant="outline" className="rounded-full border-primary text-primary hover:bg-primary/5">أضف إعلانك الآن</Button>
              </Link>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publicListings.map((listing) => {
                const media = extractMediaUrls(listing);
                const richMedia = media.length > 1 || media.some(isVideoUrl);
                const hasVideo = media.some(isVideoUrl);
                const phone = resolveContactPhone(listing);
                const waUrl = phone
                  ? buildWhatsAppUrl(
                    phone,
                    `مرحباً، رأيت إعلان "${listing.title ?? ''}" على Maknna وأرغب بالاستفسار.`,
                    country?.code || listing?.country_code || 'OM',
                  )
                  : null;
                const inboxHref = `/chat?id=listing_${listing.id}&name=${encodeURIComponent(listing.seller_name || 'المعلن')}&listing=${encodeURIComponent(listing.title || '')}`;

                return (
                  <Link href={`/marketplace/listing/${listing.id}`} key={listing.id}>
                    <Card className="overflow-hidden border-none shadow-md rounded-[2.5rem] bg-white group hover:shadow-2xl transition-all cursor-pointer">
                      <div className="relative h-60 w-full bg-slate-100 overflow-hidden">
                        {listing.images && listing.images.length > 0 ? (
                          <img src={listing.images[0].image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={listing.title} />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center opacity-30 bg-indigo-50">
                            <Package className="w-12 h-12 text-indigo-200" />
                          </div>
                        )}
                        <div className="absolute top-4 right-4 flex gap-2">
                          <Badge className="bg-white/90 text-slate-800 border-none shadow-md font-bold px-3 py-1 backdrop-blur-sm">
                            {listing.condition === 'new' ? 'جديد' : listing.condition === 'refurbished' ? 'مجدد' : 'مستعمل'}
                          </Badge>
                          {listing.is_featured && (
                            <Badge className="bg-amber-400 text-white border-none shadow-md font-bold px-3 py-1">مميز</Badge>
                          )}
                        </div>
                        {richMedia && (
                          <div className="absolute top-4 left-4">
                            <Badge className="bg-black/70 text-white border-none shadow-md font-bold px-3 py-1 backdrop-blur-sm gap-1">
                              {hasVideo ? <PlayCircle className="h-3.5 w-3.5" /> : <Images className="h-3.5 w-3.5" />}
                              {media.length} {hasVideo ? 'وسائط' : 'صور'}
                            </Badge>
                          </div>
                        )}
                        <div className="absolute bottom-4 left-4">
                          <div className="bg-emerald-600 text-white font-black px-4 py-2 rounded-2xl shadow-lg shadow-emerald-600/30">
                            {listing.price} <span className="text-[10px] font-bold opacity-80">{currencyLabel}</span>
                          </div>
                        </div>
                      </div>
                      <CardContent className="p-6">
                        <div className="mb-4">
                          <span className="text-[10px] font-black text-primary uppercase tracking-tighter bg-blue-50 px-2 py-1 rounded-lg mb-2 inline-block">
                            {listing.category_name || 'عام'}
                          </span>
                          <h3 className="text-xl font-bold font-headline leading-tight line-clamp-2 text-slate-800">{listing.title}</h3>
                        </div>

                        <div className="flex items-center gap-4 text-slate-500 text-xs mb-6">
                          <div className="flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 text-slate-400" />
                            <span>{listing.location || 'عُمان'}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <CircleDollarSign className="h-3.5 w-3.5 text-slate-400" />
                            <span>{listing.condition === 'new' ? 'جديد' : 'مستعمل'}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 border-t border-slate-50 pt-5">
                          <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-black text-xs border-2 border-white shadow-sm">
                            {listing.seller_name?.[0] || 'U'}
                          </div>
                          <div className="flex-1">
                            <p className="text-[10px] font-bold text-slate-400 uppercase">المعلن</p>
                            <p className="text-xs font-black text-slate-700">{listing.seller_name || 'مستخدم'}</p>
                          </div>
                          <Button className="rounded-xl bg-slate-900 border-none hover:bg-primary h-10 px-4 text-xs font-bold transition-all shadow-lg hover:shadow-primary/20">
                            التفاصيل
                          </Button>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              router.push(inboxHref);
                            }}
                            className="h-9 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold gap-1.5"
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                            رسالة
                          </Button>
                          <Button
                            type="button"
                            disabled={!waUrl}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!waUrl) return;
                              window.open(waUrl, '_blank', 'noopener,noreferrer');
                            }}
                            className="h-9 rounded-xl bg-green-600 hover:bg-green-700 text-white text-xs font-bold gap-1.5 disabled:opacity-50"
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                            واتساب
                          </Button>
                        </div>
                        {richMedia && (
                          <p className="mt-2 text-[11px] text-slate-500 font-semibold">
                            يحتوي على صور/فيديو إضافية، اضغط البطاقة لعرضها.
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
